import type { ErrorKind } from './error-kind'

// A leaf: this file imports only the failure kinds, so any component or owner can depend on it without depending on
// a feature.
//
// A caller that cannot tell the failure kinds apart reports `transient`, so the user can try again and the server
// still decides what is allowed. A caller that can tell them apart says so with the matching kind.

/**
 * A load as a value. `idle` means nothing has been asked for yet, which is not the same as a load in
 * flight. Empty is a property of `data`, never a separate state, and a failed load is never drawn as
 * an empty one. `refreshFailed` keeps the last good data when a later read does not succeed.
 */
export type Load<T> =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'ready'; data: T }
    | { status: 'failed'; kind: ErrorKind; error: unknown }
    | { status: 'refreshFailed'; data: T; kind: ErrorKind }

/** The data a load holds, if it holds any: ready, or the last good copy after a failed refresh. */
export const loadedData = <T>(load: Load<T>): T | undefined =>
    load.status === 'ready' || load.status === 'refreshFailed' ? load.data : undefined
